import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CurrencyInr, Plus, X, Printer, CheckCircle, FileXls, DownloadSimple, QrCode } from '@phosphor-icons/react';
import * as XLSX from 'xlsx';
import api from '../api';
import SmartPicker from '../components/SmartPicker';
import WorkflowActions from '../components/WorkflowActions';
import DraftRestoreBanner from '../components/DraftRestoreBanner';
import { useWorkflowDraft } from '../hooks/useWorkflowDraft';
import { useHighlight } from '../hooks/useHighlight';
import { useAuth } from '../contexts/AuthContext';

const fmt = n => {
  const v = Number(n) || 0;
  return v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : `₹${(v / 1000).toFixed(1)}K`;
};
const fc = n => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const INP = {
  background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,18%)',
  padding: '9px 12px', color: 'hsl(0,0%,90%)',
  fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
  outline: 'none', width: '100%', boxSizing: 'border-box',
};
const LBL = {
  fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
  color: 'hsl(0,0%,45%)', textTransform: 'uppercase',
  letterSpacing: '0.12em', marginBottom: 4, display: 'block',
};

function InvChip({ status }) {
  const map = {
    draft: 'chip-neutral', sent: 'chip-info',
    partial: 'chip-warning', paid: 'chip-active', overdue: 'chip-danger',
  };
  return <span className={map[status] || 'chip-neutral'}>{status?.toUpperCase()}</span>;
}

function printInvoice(inv) {
  const win = window.open('', '_blank', 'width=820,height=960');
  const items = (inv.items || []).map(it =>
    `<tr><td>${it.description}</td><td style="text-align:right">${it.qty}</td>
     <td style="text-align:right">₹${Number(it.unit_price).toFixed(2)}</td>
     <td style="text-align:right">₹${Number(it.total).toLocaleString('en-IN')}</td></tr>`
  ).join('');
  const balance = (inv.total_amount || 0) - (inv.paid_amount || 0);
  win.document.write(`<!DOCTYPE html><html><head><title>${inv.invoice_no}</title>
<style>
  body{font-family:monospace;padding:40px;color:#111;font-size:13px;margin:0}
  .hdr{text-align:center;border-bottom:3px solid #111;padding-bottom:16px;margin-bottom:24px}
  .hdr h1{font-size:26px;letter-spacing:.2em;margin:0;text-transform:uppercase}
  .hdr h2{font-size:13px;margin:4px 0;color:#555;text-transform:uppercase;letter-spacing:.1em}
  .row{display:flex;justify-content:space-between;margin-bottom:20px;gap:24px}
  .col{flex:1}
  .lbl{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.1em;margin-bottom:4px}
  table{width:100%;border-collapse:collapse;margin:20px 0}
  th,td{border:1px solid #ccc;padding:8px 12px;text-align:left;font-size:12px}
  th{background:#f5f5f5;font-weight:700;font-size:11px;text-transform:uppercase}
  .totals-wrap{display:flex;justify-content:flex-end}
  .totals{width:300px;border-collapse:collapse}
  .totals td{padding:6px 12px;border:none;font-size:12px}
  .totals .grand{font-weight:700;border-top:2px solid #111;padding-top:10px}
  .footer{margin-top:48px;font-size:10px;color:#aaa;border-top:1px solid #ddd;padding-top:12px;text-align:center}
  .chip{display:inline-block;background:#000;color:#fff;padding:2px 8px;font-size:10px;text-transform:uppercase;letter-spacing:.08em}
  @media print{@page{margin:18mm}}
</style></head><body>
<div class="hdr"><h1>ARIA FORGE ERP</h1><h2>Tax Invoice</h2></div>
<div class="row">
  <div class="col">
    <div class="lbl">Invoice Details</div>
    <strong>${inv.invoice_no}</strong><br>
    Date: ${new Date(inv.created_at).toLocaleDateString('en-IN')}<br>
    Due: <strong>${inv.due_date || 'N/A'}</strong><br>
    Status: <span class="chip">${inv.status?.toUpperCase()}</span>
  </div>
  <div class="col">
    <div class="lbl">Bill To</div>
    <strong>${inv.customer_name}</strong><br>
    ${inv.customer_gstin ? `GSTIN: ${inv.customer_gstin}<br>` : ''}
    ${inv.order_no ? `Order Ref: ${inv.order_no}` : ''}
  </div>
</div>
<table>
  <thead><tr><th>Description</th><th style="text-align:right">Qty</th>
  <th style="text-align:right">Unit Price</th><th style="text-align:right">Amount</th></tr></thead>
  <tbody>${items}</tbody>
</table>
<div class="totals-wrap"><table class="totals">
  <tr><td>Taxable Value</td><td style="text-align:right">${fc(inv.subtotal)}</td></tr>
  <tr><td>GST @ ${inv.gst_rate || 18}%</td><td style="text-align:right">${fc(inv.gst_amount)}</td></tr>
  <tr class="grand"><td>Total Amount</td><td style="text-align:right">${fc(inv.total_amount)}</td></tr>
  ${(inv.paid_amount || 0) > 0 ? `<tr><td>Amount Paid</td><td style="text-align:right">${fc(inv.paid_amount)}</td></tr>` : ''}
  ${balance > 0 ? `<tr><td><strong>Balance Due</strong></td><td style="text-align:right"><strong>${fc(balance)}</strong></td></tr>` : ''}
</table></div>
<div class="footer">Generated by ARIA FORGE ERP &nbsp;|&nbsp; ${new Date().toLocaleString('en-IN')}</div>
<script>window.onload=()=>window.print()</script>
</body></html>`);
  win.document.close();
}

async function downloadInvoicePdf(inv) {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    const resp = await fetch(`${backendUrl}/api/invoices/${inv.id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) throw new Error(`PDF error: ${resp.status}`);
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${inv.invoice_no}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    alert(`Failed to download PDF: ${err.message}`);
  }
}
function CreateInvoiceModal({ onClose, onCreated, customers, orders, prefill, hasDraft, restoreDraft, discardDraft, clearDraft }) {
  const [form, setForm] = useState({
    customer_id: '', customer_name: '', order_no: '', sales_order_id: '',
    item_desc: '', item_qty: '', item_price: '', gst_rate: 18, due_date: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!prefill) return;
    setForm(prev => ({
      ...prev,
      customer_id: prefill.customer_id || prev.customer_id,
      customer_name: prefill.customer_name || prev.customer_name,
      order_no: prefill.order_no || prev.order_no,
      item_desc: prefill.product_name ? `${prefill.product_name} × ${prefill.quantity} units` : prev.item_desc,
      item_qty: String(prefill.quantity || prev.item_qty || ''),
      item_price: String(prefill.invoice_amount ? (prefill.invoice_amount / (prefill.quantity || 1)).toFixed(2) : prev.item_price || ''),
    }));
  }, [prefill]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const qty = parseFloat(form.item_qty) || 0;
      const price = parseFloat(form.item_price) || 0;
      await api.post('/invoices', {
        customer_id: form.customer_id, customer_name: form.customer_name,
        order_no: form.order_no, sales_order_id: form.sales_order_id,
        items: [{ description: form.item_desc, qty, unit_price: price, total: qty * price }],
        gst_rate: parseFloat(form.gst_rate) || 18,
        due_date: form.due_date, notes: form.notes,
      });
      clearDraft && clearDraft();
      onCreated();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,20%)',
        width: '100%', maxWidth: 540, padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase' }}>Create Invoice</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none',
            cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={16} /></button>
        </div>
        <DraftRestoreBanner hasDraft={hasDraft} onRestore={restoreDraft} onDiscard={discardDraft} />
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={LBL}>Customer *</label>
              <select value={form.customer_id}
                onChange={e => { set('customer_id', e.target.value); set('customer_name', customers.find(c => c.id === e.target.value)?.name || ''); }}
                required style={{ ...INP, appearance: 'auto' }}>
                <option value="">Select Customer</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={LBL}>Linked Sales Order</label>
              <SmartPicker
                endpoint="/orders"
                params={{ status: 'ready_to_dispatch,dispatched' }}
                labelKey="order_no"
                searchKeys={['order_no', 'customer_name', 'product_name']}
                placeholder="Search orders..."
                value={form.order_no}
                onSelect={(order) => {
                  setForm(prev => ({
                    ...prev,
                    sales_order_id: order.id,
                    order_no: order.order_no,
                    item_desc: `${order.product_name} × ${order.quantity} units`,
                    item_qty: String(order.quantity),
                    item_price: String(order.unit_price),
                    customer_id: prev.customer_id || order.customer_id,
                    customer_name: prev.customer_name || order.customer_name,
                  }));
                }}
              />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={LBL}>Line Item Description *</label>
            <input value={form.item_desc} onChange={e => set('item_desc', e.target.value)}
              required placeholder="e.g. Blue Marker Box/10 × 5000 boxes" style={INP} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={LBL}>Qty *</label>
              <input type="number" value={form.item_qty}
                onChange={e => set('item_qty', e.target.value)} required style={INP} />
            </div>
            <div>
              <label style={LBL}>Unit Price ₹ *</label>
              <input type="number" step="0.01" value={form.item_price}
                onChange={e => set('item_price', e.target.value)} required style={INP} />
            </div>
            <div>
              <label style={LBL}>GST %</label>
              <input type="number" value={form.gst_rate}
                onChange={e => set('gst_rate', e.target.value)} style={INP} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div>
              <label style={LBL}>Due Date *</label>
              <input type="date" value={form.due_date}
                onChange={e => set('due_date', e.target.value)} required style={INP} />
            </div>
            <div>
              <label style={LBL}>Notes</label>
              <input value={form.notes} onChange={e => set('notes', e.target.value)}
                placeholder="Optional" style={INP} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Saving...' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PaymentModal({ invoice, onClose, onSaved }) {
  const balance = (invoice?.total_amount || 0) - (invoice?.paid_amount || 0);
  const [form, setForm] = useState({
    amount: String(Math.max(0, balance).toFixed(2)),
    payment_mode: 'NEFT', reference_no: '',
    payment_date: new Date().toISOString().split('T')[0],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/invoices/${invoice.id}/payment`, {
        invoice_id: invoice.id, invoice_no: invoice.invoice_no,
        amount: parseFloat(form.amount) || 0,
        payment_mode: form.payment_mode, reference_no: form.reference_no,
        payment_date: form.payment_date,
      });
      onSaved();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,20%)',
        width: '100%', maxWidth: 400, padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase' }}>Record Payment</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none',
            cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={16} /></button>
        </div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
          color: 'hsl(0,0%,60%)', marginBottom: 16, padding: '8px 12px',
          background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,14%)' }}>
          <span style={{ color: 'hsl(197,100%,48%)' }}>{invoice.invoice_no}</span>
          {' · '}Balance:{' '}
          <span style={{ color: 'hsl(43,96%,56%)' }}>{fc(balance)}</span>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={LBL}>Amount ₹ *</label>
              <input type="number" step="0.01" value={form.amount}
                onChange={e => set('amount', e.target.value)} required style={INP} />
            </div>
            <div>
              <label style={LBL}>Mode</label>
              <select value={form.payment_mode} onChange={e => set('payment_mode', e.target.value)}
                style={{ ...INP, appearance: 'auto' }}>
                {['NEFT', 'RTGS', 'IMPS', 'Cheque', 'Cash', 'UPI'].map(m =>
                  <option key={m} value={m}>{m}</option>
                )}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div>
              <label style={LBL}>Reference / UTR</label>
              <input value={form.reference_no} onChange={e => set('reference_no', e.target.value)}
                placeholder="UTR / Cheque No" style={INP} />
            </div>
            <div>
              <label style={LBL}>Payment Date *</label>
              <input type="date" value={form.payment_date}
                onChange={e => set('payment_date', e.target.value)} required style={INP} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Saving...' : 'Record Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const INP_STYLE = { width: '100%', background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)', padding: '8px 10px', color: 'hsl(0,0%,85%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, outline: 'none', boxSizing: 'border-box' };
const INP_LBL = { display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 };

export default function Finance() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [payingInv, setPayingInv] = useState(null);
  const [eInvoiceInv, setEInvoiceInv] = useState(null);
  const [eInvoiceForm, setEInvoiceForm] = useState({ irn: '', ack_no: '', ack_date: '', qr_data: '' });
  const [savingEInv, setSavingEInv] = useState(false);
  const [generatingIRN, setGeneratingIRN] = useState(false);
  const [irnSource, setIrnSource] = useState(null);

  const { user } = useAuth();
  const highlightId = useHighlight();
  const { prefill, hasDraft, restoreDraft, discardDraft, clearDraft } = useWorkflowDraft('finance', 'create_invoice');

  const load = async () => {
    try {
      const [invR, sumR, custR, ordR] = await Promise.all([
        api.get('/invoices'), api.get('/finance/summary'),
        api.get('/customers'), api.get('/orders'),
      ]);
      setInvoices(invR.data);
      setSummary(sumR.data);
      setCustomers(custR.data);
      setOrders(ordR.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openEInvoice = (inv) => {
    setEInvoiceInv(inv);
    setIrnSource(null);
    setEInvoiceForm({ irn: inv.irn||'', ack_no: inv.ack_no||'', ack_date: inv.ack_date||'', qr_data: inv.qr_data||'' });
  };

  const handleGenerateIRN = async () => {
    if (!eInvoiceInv) return;
    setGeneratingIRN(true);
    setIrnSource(null);
    try {
      const r = await api.post(`/invoices/${eInvoiceInv.id}/generate-irn`, { use_sandbox: true, doc_type: 'INV' });
      setEInvoiceForm({ irn: r.data.irn||'', ack_no: r.data.ack_no||'', ack_date: r.data.ack_date||'', qr_data: r.data.qr_data||'' });
      setIrnSource(r.data.irn_source || 'demo');
      await load();
      // Update local modal state with fresh data
      const fresh = r.data;
      setEInvoiceInv(prev => ({ ...prev, irn: fresh.irn, ack_no: fresh.ack_no }));
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      alert(`IRN generation failed: ${msg}`);
    }
    setGeneratingIRN(false);
  };
  const saveEInvoice = async (e) => {
    e.preventDefault();
    setSavingEInv(true);
    try {
      await api.put(`/invoices/${eInvoiceInv.id}/einvoice`, eInvoiceForm);
      await load();
      setEInvoiceInv(null);
    } catch(err) { console.error(err); }
    setSavingEInv(false);
  };

  const handleServerExport = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const resp = await fetch(`${backendUrl}/api/finance/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ARIA_Finance_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { console.error(err); alert('Export failed'); }
  };

  const exportToExcel = () => {    const rows = invoices.map(inv => ({
      'Invoice No': inv.invoice_no,
      'Customer': inv.customer_name,
      'Date': inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-IN') : '',
      'Due Date': inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-IN') : '',
      'Taxable Amount (₹)': inv.taxable_amount || 0,
      'GST Amount (₹)': inv.gst_amount || 0,
      'Total Amount (₹)': inv.total_amount || 0,
      'Status': inv.status?.toUpperCase(),
      'GST Type': inv.gst_type?.toUpperCase(),
      'GSTIN': inv.customer_gstin || '',
      'IRN': inv.irn || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const colWidths = [15,25,12,12,18,14,14,12,10,20,40];
    ws['!cols'] = colWidths.map(w => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Invoices');
    // GST Summary sheet
    if (summary) {
      const gstRows = (summary.gst_breakdown || []).map(g => ({
        'GST Rate': g.rate,
        'Taxable (₹)': g.taxable,
        'CGST (₹)': g.cgst,
        'SGST (₹)': g.sgst,
        'IGST (₹)': g.igst,
        'Total GST (₹)': g.total_gst,
      }));
      if (gstRows.length > 0) {
        const ws2 = XLSX.utils.json_to_sheet(gstRows);
        XLSX.utils.book_append_sheet(wb, ws2, 'GST Summary');
      }
    }
    XLSX.writeFile(wb, `AriaForge_Invoices_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
      <div style={{ color: 'hsl(197,100%,48%)', fontFamily: "'JetBrains Mono', monospace",
        fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.2em' }}>Loading...</div>
    </div>
  );

  const MONO_SM = { fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
    color: 'hsl(0,0%,50%)', textTransform: 'uppercase', letterSpacing: '0.12em' };

  const tabBtn = (key, label) => (
    <button key={key} onClick={() => setTab(key)} data-testid={`finance-tab-${key}`}
      style={{
        padding: '9px 20px', border: 'none', cursor: 'pointer',
        background: tab === key ? 'hsl(197,100%,48%)' : 'transparent',
        color: tab === key ? '#fff' : 'hsl(0,0%,50%)',
        fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
        textTransform: 'uppercase', letterSpacing: '0.1em', transition: 'all 0.15s',
      }}>{label}</button>
  );

  const metrics = [
    { label: 'Total Invoiced', val: fmt(summary?.total_invoiced), color: 'hsl(197,100%,48%)' },
    { label: 'Collected', val: fmt(summary?.total_collected), color: 'hsl(140,60%,45%)' },
    { label: 'Outstanding', val: fmt(summary?.total_outstanding), color: 'hsl(43,96%,56%)' },
    { label: 'Overdue', val: `${summary?.overdue_count || 0} inv (${fmt(summary?.overdue_amount)})`, color: 'hsl(0,84%,60%)' },
  ];

  return (
    <div data-testid="finance-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase', marginBottom: 4 }}>Finance</h1>
          <div style={MONO_SM}>AR / Invoices · GST Summaries · Receivables</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button data-testid="export-excel-btn" onClick={exportToExcel}
            className="btn-ghost" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px' }}>
            <FileXls size={14} /> Export Excel
          </button>
          <button data-testid="export-server-excel-btn" onClick={handleServerExport}
            className="btn-ghost" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', color: 'hsl(142,70%,55%)' }}>
            <DownloadSimple size={14} /> Full Export
          </button>
          <button data-testid="create-invoice-btn" onClick={() => navigate('/finance/invoices/new')} className="btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} weight="bold" /> New Invoice
          </button>
        </div>
      </div>

      <div className="metrics-row" style={{ marginBottom: 1, gap: 1 }}>
        {metrics.map(m => (
          <div key={m.label} className="metric-card animate-fadeInUp" style={{ flex: 1, minWidth: 160 }}>
            <div style={MONO_SM}>{m.label}</div>
            <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 24, fontWeight: 700,
              color: m.color, marginTop: 8, letterSpacing: '-0.02em' }}>{m.val}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid hsl(0,0%,14%)',
        marginTop: 1, background: 'hsl(0,0%,4%)' }}>
        {tabBtn('invoices', 'Invoices (AR)')}
        {tabBtn('gst', 'GST Summary')}
      </div>

      {tab === 'invoices' && (
        <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', marginTop: 1 }}>
          <div className="table-scroll">
            <table className="erp-table" data-testid="invoices-table">
              <thead>
                <tr>
                  <th>Invoice No</th><th>Customer</th><th>Order Ref</th>
                  <th>Taxable</th><th>GST</th><th>Total</th>
                  <th>Paid</th><th>Balance</th><th>Due Date</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 && (
                  <tr><td colSpan={11} style={{ textAlign: 'center', color: 'hsl(0,0%,35%)',
                    padding: 32 }}>No invoices yet</td></tr>
                )}
                {invoices.map(inv => {
                  const balance = (inv.total_amount || 0) - (inv.paid_amount || 0);
                  const isOverdue = inv.status !== 'paid' && inv.due_date &&
                    inv.due_date < new Date().toISOString().split('T')[0];
                  const displayStatus = isOverdue ? 'overdue' : inv.status;
                  const isHighlighted = highlightId && (inv.id === highlightId || inv.invoice_no === highlightId);
                  return (
                    <tr key={inv.id} data-testid={`inv-row-${inv.invoice_no}`}
                      style={isHighlighted ? { outline: '1px solid hsl(75,100%,50%)', boxShadow: 'inset 0 0 0 1px hsl(75,100%,50%)' } : {}}>
                      <td style={{ color: 'hsl(197,100%,48%)', fontWeight: 600 }}>{inv.invoice_no}</td>
                      <td>{inv.customer_name}</td>
                      <td style={{ color: 'hsl(0,0%,60%)' }}>{inv.order_no || '—'}</td>
                      <td>{fc(inv.subtotal)}</td>
                      <td style={{ color: 'hsl(0,0%,55%)' }}>{fc(inv.gst_amount)}</td>
                      <td style={{ fontWeight: 600 }}>{fc(inv.total_amount)}</td>
                      <td style={{ color: 'hsl(140,60%,45%)' }}>
                        {(inv.paid_amount || 0) > 0 ? fc(inv.paid_amount) : '—'}
                      </td>
                      <td style={{ color: balance > 0 ? 'hsl(43,96%,56%)' : 'hsl(0,0%,40%)' }}>
                        {balance > 0 ? fc(balance) : <CheckCircle size={12} color="hsl(140,60%,45%)" />}
                      </td>
                      <td style={{ color: isOverdue ? 'hsl(0,84%,60%)' : 'hsl(0,0%,70%)' }}>
                        {inv.due_date || '—'}
                      </td>
                      <td><InvChip status={displayStatus} /></td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          {inv.status !== 'paid' && (
                            <button onClick={() => setPayingInv(inv)}
                              data-testid={`pay-btn-${inv.invoice_no}`}
                              className="btn-ghost" style={{ padding: '3px 10px', fontSize: 10 }}>
                              Pay
                            </button>
                          )}
                          <button onClick={() => printInvoice(inv)}
                            data-testid={`print-inv-${inv.invoice_no}`}
                            className="btn-ghost" style={{ padding: '3px 8px' }}
                            title="Print invoice">
                            <Printer size={11} />
                          </button>
                          <button onClick={() => downloadInvoicePdf(inv)}
                            data-testid={`pdf-inv-${inv.invoice_no}`}
                            className="btn-ghost" style={{ padding: '3px 8px', color: 'hsl(0,84%,60%)' }}
                            title="Download PDF">
                            <DownloadSimple size={11} />
                          </button>
                          <button onClick={() => openEInvoice(inv)}
                            data-testid={`einvoice-btn-${inv.invoice_no}`}
                            className="btn-ghost" style={{ padding: '3px 8px', color: inv.irn ? 'hsl(142,70%,45%)' : 'hsl(0,0%,45%)' }}
                            title={inv.irn ? 'e-Invoice: IRN attached' : 'Attach IRN / e-Invoice'}>
                            <QrCode size={11} />
                          </button>
                          <WorkflowActions recordType="invoice" record={{ ...inv, status: inv.status }} userRole={user?.role} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'gst' && (
        <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', marginTop: 1 }}>
          <div className="table-scroll">
            <table className="erp-table" data-testid="gst-table">
              <thead>
                <tr>
                  <th>Month</th><th>No. of Invoices</th>
                  <th>Taxable Value</th><th>GST Amount</th><th>Total Invoice Value</th>
                </tr>
              </thead>
              <tbody>
                {(summary?.gst_summary || []).length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'hsl(0,0%,35%)',
                    padding: 32 }}>No GST data</td></tr>
                )}
                {(summary?.gst_summary || []).map(row => (
                  <tr key={row.month} data-testid={`gst-row-${row.month}`}>
                    <td style={{ color: 'hsl(197,100%,48%)', fontWeight: 600 }}>{row.month}</td>
                    <td>{row.count}</td>
                    <td>{fc(row.taxable)}</td>
                    <td style={{ color: 'hsl(43,96%,56%)' }}>{fc(row.gst)}</td>
                    <td style={{ fontWeight: 600 }}>{fc(row.total)}</td>
                  </tr>
                ))}
              </tbody>
              {(summary?.gst_summary || []).length > 0 && (
                <tfoot>
                  <tr>
                    <td style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                      color: 'hsl(0,0%,50%)', textTransform: 'uppercase',
                      fontWeight: 700, padding: '10px 12px' }} colSpan={2}>Totals</td>
                    <td style={{ fontWeight: 700 }}>
                      {fc((summary.gst_summary || []).reduce((s, r) => s + r.taxable, 0))}
                    </td>
                    <td style={{ fontWeight: 700, color: 'hsl(43,96%,56%)' }}>
                      {fc((summary.gst_summary || []).reduce((s, r) => s + r.gst, 0))}
                    </td>
                    <td style={{ fontWeight: 700 }}>{fc(summary?.total_invoiced || 0)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {showCreate && (
        <CreateInvoiceModal customers={customers} orders={orders}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
          prefill={prefill} hasDraft={hasDraft}
          restoreDraft={restoreDraft} discardDraft={discardDraft} clearDraft={clearDraft} />
      )}
      {payingInv && (
        <PaymentModal invoice={payingInv}
          onClose={() => setPayingInv(null)}
          onSaved={() => { setPayingInv(null); load(); }} />
      )}

      {/* e-Invoice Modal */}
      {eInvoiceInv && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(142,50%,20%)', width: 520, padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div>
                <h3 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16, textTransform: 'uppercase', color: 'hsl(142,70%,55%)', margin: 0 }}>e-Invoice / IRN</h3>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,40%)', marginTop: 4 }}>{eInvoiceInv.invoice_no} · {eInvoiceInv.customer_name}</div>
              </div>
              <button onClick={() => setEInvoiceInv(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={18} /></button>
            </div>

            {/* Auto-generate section */}
            <div style={{ marginBottom: 16, padding: '12px 14px', background: 'hsl(197,40%,5%)',
              border: '1px solid hsl(197,50%,15%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(197,100%,60%)', fontWeight: 700 }}>
                  Auto-generate via NIC/IRP Sandbox
                </div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,45%)', marginTop: 3 }}>
                  {irnSource === 'sandbox' ? 'Generated via NIC sandbox API' :
                   irnSource === 'demo' ? 'Demo IRN (SHA-256 formula) — configure NIC_CLIENT_ID for live' :
                   irnSource === 'demo_fallback' ? 'Demo IRN (NIC sandbox unreachable)' :
                   eInvoiceInv.irn ? 'IRN already attached — re-generate to replace' :
                   'Generates IRN using official SHA-256 formula or NIC sandbox'}
                </div>
              </div>
              <button onClick={handleGenerateIRN} data-testid="generate-irn-btn"
                disabled={generatingIRN || !!eInvoiceInv.irn}
                style={{ padding: '7px 14px', background: eInvoiceInv.irn ? 'hsl(0,0%,12%)' : 'hsl(197,60%,30%)',
                  border: 'none', color: eInvoiceInv.irn ? 'hsl(0,0%,40%)' : 'hsl(197,100%,75%)',
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 10, cursor: eInvoiceInv.irn ? 'not-allowed' : 'pointer',
                  textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, marginLeft: 12 }}>
                {generatingIRN ? 'Generating…' : eInvoiceInv.irn ? 'IRN Exists' : 'Generate IRN'}
              </button>
            </div>

            {irnSource && (
              <div style={{ marginBottom: 12, padding: '6px 12px', background: 'hsl(142,30%,6%)',
                border: '1px solid hsl(142,40%,15%)', fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10, color: 'hsl(142,70%,50%)' }}>
                IRN generated [{irnSource}] — review below and save to confirm.
              </div>
            )}

            <form onSubmit={saveEInvoice}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={INP_LBL}>IRN (Invoice Reference Number)</label>
                  <input value={eInvoiceForm.irn} onChange={e => setEInvoiceForm(p => ({ ...p, irn: e.target.value }))}
                    placeholder="64-char IRN from IRP portal" style={INP_STYLE} data-testid="irn-input" />
                </div>
                <div>
                  <label style={INP_LBL}>ACK Number</label>
                  <input value={eInvoiceForm.ack_no} onChange={e => setEInvoiceForm(p => ({ ...p, ack_no: e.target.value }))}
                    placeholder="Acknowledgement No." style={INP_STYLE} data-testid="ack-no-input" />
                </div>
                <div>
                  <label style={INP_LBL}>ACK Date</label>
                  <input type="date" value={eInvoiceForm.ack_date} onChange={e => setEInvoiceForm(p => ({ ...p, ack_date: e.target.value }))} style={INP_STYLE} data-testid="ack-date-input" />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={INP_LBL}>QR Code Data (optional)</label>
                  <textarea rows={2} value={eInvoiceForm.qr_data} onChange={e => setEInvoiceForm(p => ({ ...p, qr_data: e.target.value }))}
                    placeholder="QR payload from IRP..." style={{ ...INP_STYLE, resize: 'vertical' }} />
                </div>
              </div>
              <div style={{ marginTop: 16, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setEInvoiceInv(null)} className="btn-ghost">Cancel</button>
                <button type="submit" disabled={savingEInv} data-testid="save-einvoice-btn"
                  style={{ padding: '8px 20px', background: 'hsl(142,70%,40%)', border: 'none', color: '#fff',
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  {savingEInv ? 'Saving...' : 'SAVE IRN'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}